import * as React from 'react';

import { StyledDialog, StyledDialogActions } from './style';

import { AlluraErrorBoundary } from 'components/core/AlluraErrorBoundary/AlluraErrorBoundary';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { LoadingButton } from 'components/core/LoadingButton/LoadingButton';
import { StyledDialogContent } from 'components/views/forms/styles';
import { defaultConfirmText } from './constants';
import { useScreenWidth } from 'webHooks/useScreenWidth';
import { ConfirmationDialogHeader } from 'components/core/ConfirmationDialog/components/ConfirmationDialogHeader';
import { useScrollToError } from 'components/utils/ScrollToError';
import { NoSsr } from '@mui/material';
import { ScrollToTopWrapper } from 'components/core/ConfirmationDialog/components/ScrollToTopWrapper';

interface Props {
  cancelText?: string;
  children?: React.ReactNode;
  confirmButtonColor?: 'inherit' | 'secondary' | 'primary' | 'success' | 'error' | 'info' | 'warning' | undefined;
  confirmText?: string;
  variant?: 'text' | 'outlined' | 'contained';
  dialogContentText?: string;
  dialogTitle: string | React.ReactNode;
  isFullHeight?: boolean;
  isAlignTop?: boolean;
  isReverseColumn?: boolean;
  hasOverflowY?: boolean;
  greyBackground?: boolean;
  handleClickConfirm?: (e?: any) => void;
  handleCloseDialog?: (e?: any, reason?: 'backdropClick' | 'escapeKeyDown') => void;
  hasBottomBar?: boolean;
  hasFooterCancelButton?: boolean;
  hasHeaderCancelButton?: boolean;
  hasHeaderBar?: boolean;
  isDialogOpen: boolean;
  isFormikButton?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  padding?: number;
  useIsXs?: boolean;
  isFullScreen?: boolean;
  disableBackdropClick?: boolean;
  maxWidth?: false | 'sm' | 'xs' | 'md' | 'lg' | 'xl' | undefined;
  headerMenuProps?: {
    /** Ideally pass in a drawer component from mui */
    HeaderMenu: React.ReactNode;
    openHeaderMenu: () => void;
  };
  LeftHeaderComponent?: React.ReactNode;
  LeftFooterButton?: React.ReactNode;
  /** If the dialog has multiple steps then we want to scroll to top on param name change.
   * wizards are one example where we wish to do this */
  /** Optionally provide a query param name so that children of this component will scroll to top when that param changes */
  scrollParamName?: string;
  isScrollToTopOnParamChange?: boolean;
}

const ConfirmationDialog = ({
  cancelText = 'Cancel',
  children,
  confirmButtonColor,
  confirmText = defaultConfirmText,
  dialogContentText,
  dialogTitle,
  isFullHeight = false,
  isReverseColumn = false,
  isAlignTop = false,
  greyBackground = false,
  variant,
  handleClickConfirm = () => null,
  handleCloseDialog = (_e: any, _reason = 'backdropClick') => null,
  hasBottomBar = true,
  hasFooterCancelButton = true,
  LeftFooterButton,
  hasHeaderCancelButton = true,
  hasOverflowY = false,
  hasHeaderBar = true,
  isDialogOpen,
  isFormikButton = false,
  isLoading = false,
  isDisabled = false,
  padding = 16,
  disableBackdropClick = false,
  useIsXs = true,
  isFullScreen = false,
  maxWidth = 'sm',
  headerMenuProps,
  LeftHeaderComponent,
  scrollParamName,
  isScrollToTopOnParamChange = false,
}: Props) => {
  const scrollRef = React.useRef<any>(undefined);
  useScrollToError();

  const { isXs } = useScreenWidth();

  const fullScreen = React.useMemo(() => {
    if (isFullScreen) return true;
    if (useIsXs) return isXs;
    return false;
  }, [isFullScreen, isXs, useIsXs]);

  return (
    <ScrollToTopWrapper
      isScrollToTopOnParamChange={isScrollToTopOnParamChange}
      scrollParamName={scrollParamName}
      ref={scrollRef}
    >
      <StyledDialog
        onClose={(_e, reason) => {
          if (reason === 'backdropClick' && disableBackdropClick) {
            return;
          }
          handleCloseDialog();
        }}
        aria-labelledby="simple-dialog-title"
        open={isDialogOpen}
        fullWidth
        $hasOverflowY={hasOverflowY}
        $isAlignTop={isAlignTop}
        maxWidth={maxWidth}
        fullScreen={fullScreen}
        $isFullHeight={isFullHeight}
      >
        {hasHeaderBar ? (
          <ConfirmationDialogHeader
            dialogTitle={dialogTitle}
            hasHeaderCancelButton={hasHeaderCancelButton}
            close={handleCloseDialog}
            headerMenuProps={headerMenuProps}
            LeftHeaderComponent={LeftHeaderComponent}
          />
        ) : null}
        <AlluraErrorBoundary>
          {dialogContentText || children ? (
            <StyledDialogContent
              $greyBackground={greyBackground}
              $paddingHorizontal={padding}
              $paddingVertical={padding}
              $hasOverflowY={hasOverflowY}
              $isReverseColumn={isReverseColumn}
            >
              <div ref={scrollRef}></div>
              <>{dialogContentText}</>
              {children}
            </StyledDialogContent>
          ) : null}
          {hasBottomBar ? (
            <>
              <Divider />
              <StyledDialogActions>
                {LeftFooterButton}
                {hasFooterCancelButton && !LeftFooterButton ? (
                  <Button onClick={handleCloseDialog} size={isXs ? "medium" : "large"}>{cancelText}</Button>
                ) : null}
                <LoadingButton
                  isFormikButton={isFormikButton}
                  text={confirmText}
                  onClick={handleClickConfirm}
                  isLoading={isLoading}
                  isDisabled={isDisabled}
                  buttonColor={confirmButtonColor}
                  variant={!hasFooterCancelButton ? 'text' : variant ? variant : 'contained'}
                  size={isXs ? "medium" : "large"}
                />
              </StyledDialogActions>
            </>
          ) : null}
        </AlluraErrorBoundary>
      </StyledDialog>
    </ScrollToTopWrapper>
  );
};

const NoSSRDialog = (props: Props) => {
  return (
    <NoSsr>
      <ConfirmationDialog {...props} />
    </NoSsr>
  );
};

export default NoSSRDialog;
